import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    Shield, ArrowRight, CheckCircle2, Clock, Bot, Sparkles,
    Plus, ChevronRight, Check, AlertTriangle, ShieldCheck,
    Cpu, Globe, Terminal, Play, Zap, RefreshCw, X, Radio,
    FileCode, Sliders, ArrowUpRight, BarChart2, Search,
    Filter, Lock, Server, Users, ShieldAlert, CheckSquare,
    Square
} from "lucide-react";

export default function ResponseActions() {
    const navigate = useNavigate();
    const [actionsData, setActionsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedConnector, setSelectedConnector] = useState("ALL");
    const [selectedIds, setSelectedIds] = useState([]);
    const [toast, setToast] = useState(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [conditionModalAction, setConditionModalAction] = useState(null);
    const [newWorkflowForm, setNewWorkflowForm] = useState({
        workflow: "",
        description: "",
        connector: "Microsoft Defender XDR",
        dynamicSafety: "Confirmation Required",
        conditions: "None"
    });

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchActions();
    }, []);

    const fetchActions = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/respond/actions`, { headers });
            setActionsData(res.data.actions || []);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load response actions:", err);
        }
        setLoading(false);
    };

    const handleUpdateActionField = async (id, field, value) => {
        setActionsData(prev => prev.map(a => a.id === id ? { ...a, [field]: value } : a));
        try {
            await axios.patch(`${backendURL}/api/respond/actions/${id}`, { [field]: value }, { headers });
            setToast(`Updated ${field} for action #${id}`);
            setTimeout(() => setToast(null), 2000);
        } catch (err) {
            console.error("Failed to update action:", err);
        }
    };

    const handleExecuteAction = async (action) => {
        try {
            const res = await axios.post(
                `${backendURL}/api/respond/actions/${action.id}/execute`,
                { targetEntity: "Target Endpoint [desktop-PC (10.180.80.45)]" },
                { headers }
            );
            setToast(res.data.message || `Dispatched ${action.workflow}`);
            setTimeout(() => setToast(null), 4000);
        } catch (err) {
            console.error("Failed to execute action:", err);
        }
    };

    const handleCreateWorkflow = async (e) => {
        e.preventDefault();
        try {
            const res = await axios.post(`${backendURL}/api/respond/actions`, newWorkflowForm, { headers });
            if (res.data.success) {
                setActionsData(prev => [...prev, res.data.action]);
                setCreateModalOpen(false);
                setNewWorkflowForm({
                    workflow: "",
                    description: "",
                    connector: "Microsoft Defender XDR",
                    dynamicSafety: "Confirmation Required",
                    conditions: "None"
                });
                setToast("New Response Workflow Created!");
                setTimeout(() => setToast(null), 3000);
            }
        } catch (err) {
            console.error("Failed to create workflow:", err);
        }
    };

    const handleSaveConditions = (actionId, newConditionText) => {
        handleUpdateActionField(actionId, "conditions", newConditionText);
        setConditionModalAction(null);
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === filteredActions.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredActions.map(a => a.id));
        }
    };

    const toggleSelectOne = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const filteredActions = actionsData.filter(a => {
        const matchesSearch =
            a.workflow.toLowerCase().includes(searchQuery.toLowerCase()) ||
            a.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
            a.connector.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesConnector = selectedConnector === "ALL" || a.connector === selectedConnector;
        return matchesSearch && matchesConnector;
    });

    const connectorsList = [
        "FortiAnalyzer Core",
        "FortiGate Firewall",
        "ThreatDown / Malwarebytes OneView",
        "Microsoft Defender XDR"
    ];

    const connectedConnectors = [
        "FortiAnalyzer Core",
        "FortiGate Firewall",
        "ThreatDown / Malwarebytes OneView",
        "Microsoft Defender XDR"
    ];

    const safetyOptions = [
        "Automatic",
        "Confirmation Required",
        "Customer Confirmation Required"
    ];

    return (
        <div className="w-full space-y-6 pb-12">
            {/* ─── Toast Feedback ─── */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-4 py-2.5 rounded-xl shadow-2xl text-[12px] font-bold flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-200">
                    <Check className="w-4 h-4" />
                    {toast}
                </div>
            )}

            {/* ─── Breadcrumb ─── */}
            <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                <span>02</span>
                <span>/</span>
                <span className="text-slate-400">Response Actions</span>
            </div>

            {/* ─── Main Hero Layout (2 Columns) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                
                {/* ═══ LEFT HERO DESCRIPTION (4.5 cols) ═══ */}
                <div className="lg:col-span-4 space-y-6">
                    <div>
                        <h1 className="text-[28px] lg:text-[34px] font-black text-white tracking-tight leading-tight">
                            The exact next step, ready to run.
                        </h1>
                        <p className="text-[13.5px] text-slate-300 mt-3 leading-relaxed">
                            For every confirmed threat, QueryTel lays out the specific actions to contain it, <strong>revoke the session, isolate the host, block the perimeter IP</strong>, and runs them against the tools you already own. You approve what executes. Humans stay on the loop.
                        </p>
                    </div>

                    <div className="space-y-2.5 pt-2">
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Concrete actions, not vague recommendations.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Executed against FortiAnalyzer & FortiGate firewalls.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Approval gates so a person stays in control.</span>
                        </div>
                    </div>

                    {/* Summary Card */}
                    <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-sm space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-[12px] font-bold text-white uppercase tracking-wider">Playbook Inventory</span>
                            <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                                Connected Connectors: {connectedConnectors.length} Active
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-[12px]">
                            <div className="p-3 bg-black/30 rounded-xl border border-white/[0.04]">
                                <span className="text-[10px] text-slate-500 font-mono uppercase block">Active Playbooks</span>
                                <span className="text-[18px] font-bold text-white font-mono">
                                    {actionsData.filter(a => a.status === "ENABLED").length} of {actionsData.length}
                                </span>
                            </div>
                            <div className="p-3 bg-black/30 rounded-xl border border-white/[0.04]">
                                <span className="text-[10px] text-slate-500 font-mono uppercase block">Fully Autonomous</span>
                                <span className="text-[18px] font-bold text-blue-400 font-mono">
                                    {actionsData.filter(a => a.dynamicSafety === "Automatic").length} Actions
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ═══ RIGHT WORKBENCH: Response Actions Table (7.5 cols) ═══ */}
                <div className="lg:col-span-8 bg-[#111827] border border-white/[0.06] rounded-2xl p-6 shadow-2xl space-y-6">
                    
                    {/* Header Controls */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/[0.06] pb-4">
                        <div className="flex items-center gap-2 flex-1">
                            {/* Search */}
                            <div className="flex items-center bg-black/40 border border-white/[0.08] rounded-lg px-3 py-1.5 flex-1 focus-within:border-blue-500/40">
                                <Search className="w-3.5 h-3.5 text-slate-500 mr-2" />
                                <input
                                    type="text"
                                    placeholder="Search actions..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="bg-transparent border-none outline-none text-[12px] text-white placeholder:text-slate-600 w-full"
                                />
                            </div>

                            {/* Connector Filter */}
                            <select
                                value={selectedConnector}
                                onChange={(e) => setSelectedConnector(e.target.value)}
                                className="bg-[#1E293B] text-slate-200 border border-white/[0.12] text-[11px] font-medium rounded-lg px-2.5 py-1.5 outline-none cursor-pointer"
                            >
                                <option value="ALL">All Connectors ({connectedConnectors.length} Connected)</option>
                                {connectorsList.map((c, idx) => (
                                    <option key={idx} value={c}>
                                        {connectedConnectors.includes(c) ? `🟢 ${c} (Connected)` : `⚪ ${c} (Available)`}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCreateModalOpen(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[11.5px] font-bold shadow-md transition-all whitespace-nowrap"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Create custom workflow
                            </button>
                        </div>
                    </div>

                    {/* Table View matching screenshot 02 */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse text-[12px]">
                            <thead className="border-b border-white/[0.06] bg-black/40 text-[10.5px] uppercase font-mono text-slate-400">
                                <tr>
                                    <th className="py-3 px-3 w-8">
                                        <button onClick={toggleSelectAll} className="text-slate-400 hover:text-white">
                                            {selectedIds.length === filteredActions.length && filteredActions.length > 0 ? (
                                                <CheckSquare className="w-3.5 h-3.5 text-blue-400" />
                                            ) : (
                                                <Square className="w-3.5 h-3.5" />
                                            )}
                                        </button>
                                    </th>
                                    <th className="py-3 px-3">Status</th>
                                    <th className="py-3 px-3">Workflow</th>
                                    <th className="py-3 px-4">Description</th>
                                    <th className="py-3 px-3">Connectors</th>
                                    <th className="py-3 px-3">Dynamic safety</th>
                                    <th className="py-3 px-3">Conditions</th>
                                    <th className="py-3 px-3 text-right">Test</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/[0.04]">
                                {filteredActions.map((action) => (
                                    <tr key={action.id} className="hover:bg-white/[0.02] transition-colors group">
                                        
                                        {/* Checkbox */}
                                        <td className="py-3 px-3">
                                            <button onClick={() => toggleSelectOne(action.id)} className="text-slate-400 hover:text-white">
                                                {selectedIds.includes(action.id) ? (
                                                    <CheckSquare className="w-3.5 h-3.5 text-blue-400" />
                                                ) : (
                                                    <Square className="w-3.5 h-3.5" />
                                                )}
                                            </button>
                                        </td>

                                        {/* Status */}
                                        <td className="py-3 px-3 whitespace-nowrap">
                                            <button
                                                onClick={() => handleUpdateActionField(
                                                    action.id,
                                                    "status",
                                                    action.status === "ENABLED" ? "DISABLED" : "ENABLED"
                                                )}
                                                className={`text-[9.5px] font-mono uppercase px-2 py-0.5 rounded font-bold border transition-colors ${
                                                    action.status === "ENABLED"
                                                        ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                                                        : "bg-slate-700/30 text-slate-400 border-white/[0.08]"
                                                }`}
                                            >
                                                {action.status}
                                            </button>
                                        </td>

                                        {/* Workflow Name */}
                                        <td className="py-3 px-3 whitespace-nowrap font-bold text-white">
                                            {action.workflow}
                                        </td>

                                        {/* Description */}
                                        <td className="py-3 px-4 text-slate-400 text-[11.5px] max-w-xs truncate" title={action.description}>
                                            {action.description}
                                        </td>

                                        {/* Connector Dropdown */}
                                        <td className="py-3 px-3 whitespace-nowrap">
                                            <select
                                                value={action.connector}
                                                onChange={(e) => handleUpdateActionField(action.id, "connector", e.target.value)}
                                                className="bg-[#1E293B] text-slate-200 border border-white/[0.1] text-[11px] rounded-lg px-2 py-1 outline-none cursor-pointer"
                                            >
                                                {connectorsList.map((c, idx) => (
                                                    <option key={idx} value={c} className="bg-[#0F172A]">
                                                        {connectedConnectors.includes(c) ? `🟢 ${c}` : `⚪ ${c}`}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>

                                        {/* Dynamic Safety Dropdown */}
                                        <td className="py-3 px-3 whitespace-nowrap">
                                            <select
                                                value={action.dynamicSafety}
                                                onChange={(e) => handleUpdateActionField(action.id, "dynamicSafety", e.target.value)}
                                                className={`border text-[11px] font-medium rounded-lg px-2 py-1 outline-none cursor-pointer ${
                                                    action.dynamicSafety === "Automatic"
                                                        ? "bg-blue-950/60 text-blue-300 border-blue-500/30"
                                                        : "bg-[#1E293B] text-slate-300 border-white/[0.1]"
                                                }`}
                                            >
                                                {safetyOptions.map((s, idx) => (
                                                    <option key={idx} value={s} className="bg-[#0F172A]">{s}</option>
                                                ))}
                                            </select>
                                        </td>

                                        {/* Conditions */}
                                        <td className="py-3 px-3 whitespace-nowrap">
                                            <button
                                                onClick={() => setConditionModalAction(action)}
                                                className="text-[11px] font-medium text-blue-400 hover:text-blue-300 hover:underline"
                                            >
                                                {action.conditions && action.conditions !== "None" ? action.conditions : "Add conditions"}
                                            </button>
                                        </td>

                                        {/* 1-Click Test Execution */}
                                        <td className="py-3 px-3 text-right whitespace-nowrap">
                                            <button
                                                onClick={() => handleExecuteAction(action)}
                                                className="p-1.5 bg-blue-500/10 hover:bg-blue-600 hover:text-white text-blue-400 rounded-lg transition-all"
                                                title={`Test execute ${action.workflow}`}
                                            >
                                                <Zap className="w-3.5 h-3.5" />
                                            </button>
                                        </td>

                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                </div>

            </div>

            {/* ─── Condition Editor Modal ─── */}
            {conditionModalAction && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-md p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <h3 className="text-[14px] font-bold text-white">
                                Execution Conditions: {conditionModalAction.workflow}
                            </h3>
                            <button onClick={() => setConditionModalAction(null)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="space-y-3 text-[12px]">
                            <p className="text-slate-400 text-[11.5px]">
                                Configure fine-grained safety boundaries and exclusions for this containment playbook.
                            </p>

                            <div className="space-y-2">
                                {[
                                    "Exclude Domain Controllers (dc-01, dc-02)",
                                    "Only execute if Severity is CRITICAL",
                                    "Require human approval if user is on VIP List",
                                    "Bypass execution outside business hours"
                                ].map((cond, cIdx) => (
                                    <label key={cIdx} className="flex items-center gap-2 p-2.5 bg-black/30 border border-white/[0.04] rounded-lg cursor-pointer hover:bg-white/[0.02]">
                                        <input type="checkbox" defaultChecked={cIdx < 2} className="rounded bg-black text-blue-600" />
                                        <span className="text-slate-200">{cond}</span>
                                    </label>
                                ))}
                            </div>

                            <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/[0.06]">
                                <button
                                    onClick={() => setConditionModalAction(null)}
                                    className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleSaveConditions(conditionModalAction.id, "Configured (2 rules)")}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-md"
                                >
                                    Save Conditions
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Create Custom Workflow Modal ─── */}
            {createModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <h3 className="text-[15px] font-bold text-white">Create Response Action Workflow</h3>
                            <button onClick={() => setCreateModalOpen(false)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <form onSubmit={handleCreateWorkflow} className="space-y-3.5 text-[12px]">
                            <div>
                                <label className="text-slate-400 block mb-1">Workflow Name</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Invalidate AWS IAM Credentials"
                                    value={newWorkflowForm.workflow}
                                    onChange={(e) => setNewWorkflowForm({ ...newWorkflowForm, workflow: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Description</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Deactivate compromised access keys in AWS IAM"
                                    value={newWorkflowForm.description}
                                    onChange={(e) => setNewWorkflowForm({ ...newWorkflowForm, description: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-slate-400 block mb-1">Connector</label>
                                    <select
                                        value={newWorkflowForm.connector}
                                        onChange={(e) => setNewWorkflowForm({ ...newWorkflowForm, connector: e.target.value })}
                                        className="w-full bg-[#1E293B] border border-white/[0.12] rounded-lg px-3 py-2 text-white outline-none"
                                    >
                                        {connectorsList.map((c, idx) => (
                                            <option key={idx} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="text-slate-400 block mb-1">Dynamic Safety Mode</label>
                                    <select
                                        value={newWorkflowForm.dynamicSafety}
                                        onChange={(e) => setNewWorkflowForm({ ...newWorkflowForm, dynamicSafety: e.target.value })}
                                        className="w-full bg-[#1E293B] border border-white/[0.12] rounded-lg px-3 py-2 text-white outline-none"
                                    >
                                        {safetyOptions.map((s, idx) => (
                                            <option key={idx} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/[0.06]">
                                <button
                                    type="button"
                                    onClick={() => setCreateModalOpen(false)}
                                    className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-md"
                                >
                                    Save Playbook
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
